/**
 * Shower Filter results stats count-up.
 *
 * All four numbers are driven by one requestAnimationFrame loop and one eased
 * progress value, so they start together and finish together — the customer
 * never waits on a stagger, and the group cannot drift out of step.
 *
 * Scoped to <sf-results>: queries run against the element, so multiple
 * instances coexist and a Theme Editor section reload re-initialises cleanly.
 */
const SFR_REDUCED = '(prefers-reduced-motion: reduce)';

class SfResults extends HTMLElement {
  connectedCallback() {
    this.counters = Array.from(this.querySelectorAll('[data-sfr-count]'));
    if (!this.counters.length) return;

    this.duration = Math.max(100, Number(this.dataset.duration) || 1400);
    this.once = this.dataset.once !== 'false';
    this.done = false;

    // Reduced motion: show the figures at once. They are the content, not
    // decoration, so they must not depend on an animation running.
    if (window.matchMedia(SFR_REDUCED).matches) {
      this.#settle();
      return;
    }

    this.#reset();

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.#play();
          if (this.once) this.observer.disconnect();
        });
      },
      { threshold: 0.35 }
    );
    this.observer.observe(this);
  }

  #format(el, value) {
    const decimals = Math.min(2, Math.max(0, Number(el.dataset.decimals) || 0));
    const n = value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${el.dataset.prefix || ''}${n}${el.dataset.suffix || ''}`;
  }

  #reset() {
    this.counters.forEach((el) => {
      el.textContent = this.#format(el, 0);
    });
  }

  #settle() {
    this.counters.forEach((el) => {
      el.textContent = this.#format(el, Number(el.dataset.target) || 0);
    });
    this.done = true;
  }

  #play() {
    if (this.running || (this.once && this.done)) return;
    this.running = true;

    const start = performance.now();
    // Ease-out cubic: quick off the mark, settling onto the figure. Lands
    // exactly on 1, so the final value cannot overshoot the target.
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const frame = (now) => {
      const t = Math.min(1, (now - start) / this.duration);
      const e = ease(t);

      this.counters.forEach((el) => {
        const target = Number(el.dataset.target) || 0;
        el.textContent = this.#format(el, target * e);
      });

      if (t < 1) {
        this.frame = requestAnimationFrame(frame);
        return;
      }

      // Land on the configured figures exactly, rather than on whatever the
      // last interpolated frame happened to produce.
      this.#settle();
      this.running = false;
    };

    this.frame = requestAnimationFrame(frame);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('sf-results')) customElements.define('sf-results', SfResults);
